// src/pages/msp-inventory/hooks/useImageBackfill.js
import { useEffect, useRef, useMemo } from "react";
import { API_BASE } from "../../../consts";
import authService from "../../../services/AuthService";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);

const BACKFILL_STAGGER_MS = 400;

// Slowly, one at a time, top-to-bottom, backfills missing images (+ brand)
// for any list of report rows that carry an id but no image_url yet, via
// the existing /api/walmart/api/item-details endpoint — persists to
// catalog_items for good, so every other page benefits afterward.
//
// IMPORTANT: the restart trigger is a STABLE KEY built from the ordered list
// of ids (idsKey), NOT the raw `items` array. This is deliberate — every
// successful backfill calls patchItem, which creates a brand-new `items`
// array reference (image_url just got filled in), and if the effect
// depended on that reference directly, each patch would race-cancel the
// still-running loop before it could set runningRef back to false, killing
// the whole backfill after just one or two rows. Keying off idsKey means
// the effect only restarts when the actual SET or ORDER of ids changes
// (new report loaded, re-sorted, basis/group toggled) — not when a row's
// own image_url field gets patched in.
export const useImageBackfill = (items, patchItem, { idKey = "catalog_item_id" } = {}) => {
    const runningRef = useRef(false);
    const doneIdsRef = useRef(new Set());
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const idsKey = useMemo(
        () => (items || []).map((i) => i[idKey]).filter(Boolean).join(","),
        [items, idKey]
    );

    useEffect(() => {
        if (!idsKey || runningRef.current) return;
        let cancelled = false;
        runningRef.current = true;

        const idsInOrder = idsKey.split(",");

        (async () => {
            for (const id of idsInOrder) {
                if (cancelled) break;
                if (doneIdsRef.current.has(id)) continue;

                // Read the CURRENT item via the ref, not a closed-over value —
                // reflects any patches applied since this loop started,
                // without needing the effect itself to restart.
                const current = (itemsRef.current || []).find((r) => r[idKey] === id);
                // image_url === "" means we already tried once (backend
                // persisted the failure) and found nothing — treat that as
                // done, same as having a real image. Only null/undefined
                // (never attempted at all) should trigger another fetch.
                const alreadyResolved = current && (current.image_url || current.image_url === "");
                if (!current || alreadyResolved) {
                    doneIdsRef.current.add(id);
                    continue;
                }

                doneIdsRef.current.add(id);
                try {
                    const resp = await api(`/api/walmart/api/item-details?catalog_item_id=${encodeURIComponent(id)}`);
                    const data = await resp.json();
                    if (resp.ok && !data.error) {
                        patchItem(id, { image_url: data.image_url || undefined, brand: data.brand || undefined });
                    } else if (data.image_url === "") {
                        // Backend confirmed + persisted "no image exists" —
                        // reflect that locally too so this row renders as
                        // resolved (no spinner/retry) for the rest of this
                        // session, not just on the next page load.
                        patchItem(id, { image_url: "" });
                    }
                    // any other failure shape (network-level 500, account not
                    // linked, etc) — non-fatal, row stays imageless and MAY
                    // retry on a future full reload, since we didn't persist
                    // a "never found" verdict for those cases
                } catch {
                    // network hiccup — non-fatal, next full reload may retry
                }
                await new Promise((res) => setTimeout(res, BACKFILL_STAGGER_MS));
            }
            runningRef.current = false;
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);
};